import { IMAGE_RULES } from '@td/shared';

/**
 * Shrinks and converts an image to WebP in the browser before upload, so a
 * multi-megabyte phone photo never crosses the network at full size.
 *
 * This is a convenience, not a security control — the server re-validates and
 * re-encodes everything it receives regardless of what arrives here.
 */
export async function resizeToWebp(file: File, size: number): Promise<Blob> {
  if (!IMAGE_RULES.acceptedMimeTypes.includes(file.type as never) && file.type !== '') {
    throw new Error('Use a JPEG, PNG, or WebP image.');
  }

  const bitmap = await createImageBitmap(file).catch(() => {
    throw new Error("That image couldn't be read.");
  });

  // Cover-fit: fill the square, cropping the overflow rather than letterboxing.
  const scale = Math.max(size / bitmap.width, size / bitmap.height);
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Your browser could not process that image.');

  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bitmap, (size - w) / 2, (size - h) / 2, w, h);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/webp', 0.85),
  );
  if (!blob) throw new Error('Could not convert that image.');

  return blob;
}
